import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MapView, { BASEMAPS } from "@/components/MapView";
import DetailsPanel from "@/components/workspace/DetailsPanel";
import BoundaryPanel from "@/components/workspace/BoundaryPanel";
import CRSPanel from "@/components/workspace/CRSPanel";
import SatellitePanel from "@/components/workspace/SatellitePanel";
import ProcessingPanel from "@/components/workspace/ProcessingPanel";
import MapLegend from "@/components/workspace/MapLegend";
import IndicesPanel from "@/components/workspace/IndicesPanel";
import PcaPanel from "@/components/workspace/PcaPanel";
import TerrainPanel from "@/components/workspace/TerrainPanel";
import GeoPanel from "@/components/workspace/GeoPanel";
import MLPanel from "@/components/workspace/MLPanel";
import MapStudioPanel from "@/components/workspace/MapStudioPanel";
import TargetsPanel from "@/components/workspace/TargetsPanel";
import FieldPlanPanel from "@/components/workspace/FieldPlanPanel";
import ReportExportPanel from "@/components/workspace/ReportExportPanel";
import { CaretLeft, CaretRight, ArrowLeft, Stack } from "@phosphor-icons/react";

const STEPS = [
  { n: 1, label: "Create Project", panel: "details", phase: "Setup" },
  { n: 2, label: "Project Details", panel: "details", phase: "Setup" },
  { n: 3, label: "Upload / Draw Boundary", panel: "boundary", phase: "Setup" },
  { n: 4, label: "Validate Coordinates & CRS", panel: "crs", phase: "Setup" },
  { n: 5, label: "Select Satellite Data", panel: "satellite", phase: "Imagery" },
  { n: 6, label: "Search Online Imagery", panel: "satellite", phase: "Imagery" },
  { n: 7, label: "Select Date & Cloud Cover", panel: "satellite", phase: "Imagery" },
  { n: 8, label: "Stream Satellite Bands", panel: "processing", phase: "Imagery" },
  { n: 9, label: "Pre-process Imagery", panel: "processing", phase: "Imagery" },
  { n: 10, label: "Generate Band Combinations", panel: "indices", phase: "Analysis" },
  { n: 11, label: "Generate Mineral Indices", panel: "indices", phase: "Analysis" },
  { n: 12, label: "Terrain & Structures", panel: "terrain", phase: "Analysis" },
  { n: 13, label: "Upload Geological Data", panel: "geodata", phase: "Analysis" },
  { n: 14, label: "Run Mineral Targeting", panel: "targets", phase: "Targeting" },
  { n: 15, label: "AI / ML Prospectivity", panel: "ml", phase: "Targeting" },
  { n: 16, label: "Review & Edit Targets", panel: "targets", phase: "Targeting" },
  { n: 17, label: "Map Studio", panel: "mapstudio", phase: "Deliverables" },
  { n: 18, label: "Technical Report", panel: "report", phase: "Deliverables" },
  { n: 19, label: "Export GIS Files", panel: "export", phase: "Deliverables" },
  { n: 20, label: "Project Package", panel: "export", phase: "Deliverables" },
];
const ORDER = ["details", "boundary", "crs", "satellite", "processing", "indices", "terrain", "geodata", "targets", "ml", "mapstudio", "report", "export"];

export default function ProjectWorkspace() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [panel, setPanel] = useState("details");
  const [basemap, setBasemap] = useState("satellite");
  const [overlay, setOverlay] = useState(null);
  const [opacity, setOpacity] = useState(0.75);
  const [drawActive, setDrawActive] = useState(false);
  const [drawPoints, setDrawPoints] = useState([]);

  const reload = useCallback(() => api.get(`/projects/${id}`).then((r) => setProject(r.data)).catch(() => {}), [id]);
  useEffect(() => { reload(); api.get("/catalog").then((r) => setCatalog(r.data)).catch(() => {}); }, [id, reload]);

  if (!project) return <div className="p-8 text-muted-foreground">Loading project…</div>;

  const boundary = project.boundary ? { type: "Feature", geometry: project.boundary, properties: {} } : null;
  const bbox = project.boundary_metrics?.bbox;
  const idx = ORDER.indexOf(panel);

  const panelTitle = STEPS.find((s) => s.panel === panel)?.label || "";
  const renderPanel = () => {
    switch (panel) {
      case "details": return <DetailsPanel project={project} reload={reload} catalog={catalog} />;
      case "boundary": return <BoundaryPanel project={project} reload={reload} drawActive={drawActive} setDrawActive={setDrawActive} drawPoints={drawPoints} setDrawPoints={setDrawPoints} />;
      case "crs": return <CRSPanel project={project} reload={reload} catalog={catalog} />;
      case "satellite": return <SatellitePanel project={project} reload={reload} catalog={catalog} />;
      case "processing": return <ProcessingPanel project={project} reload={reload} />;
      case "indices": return (<div className="space-y-4"><IndicesPanel project={project} catalog={catalog} setOverlay={setOverlay} activeOverlayKey={overlay?.key} /><PcaPanel project={project} reload={reload} setOverlay={setOverlay} activeOverlayKey={overlay?.key} /></div>);
      case "terrain": return <TerrainPanel project={project} reload={reload} setOverlay={setOverlay} activeOverlayKey={overlay?.key} drawActive={drawActive} setDrawActive={setDrawActive} drawPoints={drawPoints} setDrawPoints={setDrawPoints} />;
      case "geodata": return <GeoPanel project={project} reload={reload} setOverlay={setOverlay} activeOverlayKey={overlay?.key} />;
      case "ml": return <MLPanel project={project} reload={reload} setOverlay={setOverlay} activeOverlayKey={overlay?.key} />;
      case "mapstudio": return <MapStudioPanel project={project} />;
      case "targets": return (<div className="space-y-6"><TargetsPanel project={project} reload={reload} catalog={catalog} /><FieldPlanPanel project={project} reload={reload} /></div>);
      case "report": return <ReportExportPanel project={project} catalog={catalog} mode="report" />;
      case "export": return <ReportExportPanel project={project} catalog={catalog} mode="export" />;
      default: return <p className="text-sm text-muted-foreground">This module is Coming in Phase 2 (terrain/DEM, lineament extraction, geological & geochemical data upload, supervised ML).</p>;
    }
  };

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)] lg:h-[calc(100vh-4rem)] overflow-x-hidden">
      {/* header */}
      <div className="min-h-14 border-b border-border flex items-center justify-between flex-wrap gap-2 px-4 sm:px-5 py-2 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => nav("/projects")} data-testid="back-projects"><ArrowLeft size={16} /></Button>
          <div className="min-w-0">
            <div className="font-display font-bold truncate">{project.name}</div>
            <div className="text-[11px] text-muted-foreground font-mono">{project.commodity} · {project.country || "-"} · {project.report_number}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => nav(`/projects/${id}`)} data-testid="to-wizard">Wizard mode</Button>
          <Badge variant="outline">{project.status}</Badge>
          <Button variant="outline" size="sm" disabled={idx <= 0} onClick={() => setPanel(ORDER[idx - 1])} data-testid="wf-prev"><CaretLeft size={14} className="mr-1" />Previous</Button>
          <Button size="sm" disabled={idx >= ORDER.length - 1} onClick={() => setPanel(ORDER[idx + 1])} data-testid="wf-next">Next<CaretRight size={14} className="ml-1" /></Button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row flex-1 min-h-0">
        {/* timeline */}
        <div className="hidden lg:block w-56 border-r border-border overflow-y-auto shrink-0 py-3">
          {["Setup", "Imagery", "Analysis", "Targeting", "Deliverables"].map((ph) => (
            <div key={ph} className="mb-2">
              <div className="px-4 py-1 overline">{ph}</div>
              {STEPS.filter((s) => s.phase === ph).map((s) => {
                const active = s.panel === panel;
                return (
                  <button key={s.n} onClick={() => setPanel(s.panel)} data-testid={`step-${s.n}`}
                    className={`w-full text-left px-4 py-1.5 text-xs flex items-center gap-2 transition-colors ${active ? "text-primary bg-primary/10 border-l-2 border-primary" : "text-muted-foreground hover:text-foreground border-l-2 border-transparent"}`}>
                    <span className="font-mono opacity-60 w-4">{s.n}</span>{s.label}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* step panel */}
        <div className="w-full lg:w-[400px] border-b lg:border-b-0 lg:border-r border-border flex flex-col shrink-0 min-h-0 max-h-[45vh] lg:max-h-none">
          <div className="px-5 py-3 border-b border-border shrink-0">
            <p className="overline">Step</p>
            <h2 className="font-display font-bold">{panelTitle}</h2>
          </div>
          <div className="flex-1 overflow-y-auto p-5">{renderPanel()}</div>
        </div>

        {/* map */}
        <div className="flex-1 relative min-w-0 h-[55vh] lg:h-auto">
          <MapView
            boundary={boundary} bbox={bbox} basemap={basemap}
            overlay={overlay} overlayOpacity={opacity}
            targets={["targets", "indices", "ml", "geodata"].includes(panel) ? ((project.canonical_targets?.length ? project.canonical_targets : project.targets) || []) : []}
            samplePoints={["geodata", "ml"].includes(panel) ? project.field_data || [] : []}
            planPoints={panel === "targets" ? (project.field_plan?.points || []) : []}
            planRoutes={panel === "targets" ? (project.field_plan?.routes || []) : []}
            lineamentFeatures={panel === "terrain" ? [...(project.lineaments?.features || []), ...(project.sar_lineaments?.features || [])] : []}
            drawActive={drawActive} drawPoints={drawPoints}
            onAddPoint={(p) => setDrawPoints((prev) => [...prev, p])}
            center={bbox ? [(bbox[1] + bbox[3]) / 2, (bbox[0] + bbox[2]) / 2] : [-2.85, 32.25]}
          />
          <MapLegend overlay={overlay} profile={(catalog?.commodity_profiles || {})[project.commodity]} />
          {/* map controls */}
          <div className="absolute top-3 right-3 z-[800] bg-card border border-border rounded-md p-2 flex flex-col gap-2 w-40 max-w-[42vw] shadow-lg">
            <div>
              <p className="overline mb-1">Basemap</p>
              <Select value={basemap} onValueChange={setBasemap}>
                <SelectTrigger className="h-8 text-xs" data-testid="map-basemap"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(BASEMAPS).map(([k, v]) => <SelectItem key={k} value={k}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {overlay && (
              <div>
                <p className="overline mb-1">Layer opacity</p>
                <Slider value={[opacity]} min={0} max={1} step={0.05} onValueChange={(v) => setOpacity(v[0])} data-testid="map-opacity" />
                <div className="text-[10px] font-mono text-muted-foreground mt-1 truncate">{overlay.key}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="h-8 border-t border-border flex items-center px-5 shrink-0">
        <p className="text-[10px] text-muted-foreground font-mono truncate">
          {catalog?.disclaimer}
        </p>
      </div>
    </div>
  );
}
